import React, { useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Button,
  IconButton
} from "@mui/material";
import { getLocalizedText } from "../../logic/Utils";
import { makeStyles, useTheme } from "@mui/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Carousel from "react-elastic-carousel";
import SectionLayout from "../../layout/SectionLayout";
import { SectionTitle, SectionSubtitle } from "../../../Template3/components/SectionTitle";
import NoImage from "src/assets/images/no-pictures.png";
import useServiceCard from "../../logic/useServiceCard";
import { useCompanyData } from "../../contexts/DataContext";
import { textVariant, textClasses } from "../../config";

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    width: "100%",
    backgroundColor: "#FFFFFF", 
  },
  headingsContainer: {
    display:"flex",
    flexDirection:"column",
    alignItems:"center",
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  carouselWrapper: {
    position: "relative",
    width: "100%",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    "& .rec-carousel-item": {
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding:'10px 0px'
    },
    "& .rec-carousel-wrapper": {
      padding: "1rem 0",
    },
    "& .rec-arrow": {
      backgroundColor: "rgba(255,255,255,0.8)",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,1)",
      },
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
    },
    "& .rec-pagination": {
      display: "none",
    },
  },
  arrowButtonPrev: {
    position: "absolute !important",
    left: -20,
    top: "45%",
    zIndex: 10,
  },
  arrowButtonNext: {
    position: "absolute !important",
    right: -20,
    top: "45%",
    zIndex: 10,
  },
  viewAllWrapper: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  viewAllButton: {
    backgroundColor: '#F4F6F6',
    borderRadius: 50,
    padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
    color: "#0F2240",
    textTransform: "none",
    border:'1px solid #F2F2F2',
    textTransform: "capitalize",
    transition: "background-color 0.3s ease",
    fontWeight: 600,
    width:'calc(229px - 48px)',
      height:63,
    "&:hover": {
      backgroundColor: '#ccc',
    },
  },
  cardRoot: {
    minWidth: "calc(208px - 30px)",
    maxWidth: "calc(208px - 30px)",
    minHeight: 'calc(245px - 28px)',
    height:'100%',
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "none",
    padding:'12px 15px 16px 15px',
    transition: "transform 0.3s, box-shadow 0.3s",
   "&:hover": {
      transform: "scale(1.03)",
      border: "1px solid #F2F2F2",
      boxShadow: "3px 6px 10px rgba(0,0,0,0.1)",
    },
    "&:hover $underline::after": {
      content: '""',
      width: "40px",
      height: "2px",
      backgroundColor: "#D8D8D8",
      position: "absolute",
      bottom: -4,
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  avatarWrapper: {
    width: 160,
    height: 160,
    borderRadius: "50%",
    overflow: "hidden",
    flexShrink: 0,
  },
  avatarImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  cardTitleWrapper: {
    position: "relative",
    display: "inline-block", // needed so ::after doesn't stretch full width
    marginTop: theme.spacing(2),
    textAlign: "center",
     display:"flex",
    alignItems:"center",
    justifyContent:"center",
    height:"85px",
      width:"100%"
  },
  cardTitle: {
    fontSize: "18px",
    fontWeight: 500,
    color: "#0F2240",
    textTransform: "capitalize",
  },
  underline: {
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      width: 0,
      height: "2px",
      backgroundColor: "transparent",
      transition: "all 0.3s ease",
      bottom: -4,
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
}));

export function Services({
  services,
  categories,
  t,
  handleExploreServices,
  handleExploreCategories,
  productType,
  data: dataObj,
  mainColor,
  contentData,
  index,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(false);
  const { chatgptContent } = dataObj || {};
  const companyId = dataObj?.companyData?.companyId || "";

  // 1) Titles / subtitles from contentData (or fallback)
  const {
    title,
    arTitle,
    subTitle,
    arSubTitle,
    maxDisplayItems,
    titleFontSize,
    subTitleFontSize,
    titleFontColor,
    titleFormat,
    subTitleFontColor,
    backgroundStyle,
    backgroundColor,
    backgroundImage,
    paddingTop,
    paddingBottom,
  } = contentData || {};

  const maxServiceShow = maxDisplayItems ? maxDisplayItems : null;

  const isRTL = dataObj?.isRTL === "ar";
  const _title = title
  ? getLocalizedText(title, arTitle)
   : productType === 'categories'
    ? t?.("companyPage.exploreAllCategories")
    : t?.("companyPage.exploreAllServices");

   const _subTitle = subTitle
    ? getLocalizedText(subTitle, arSubTitle)
    : t?.("companyPage.exploreTitle2");

  // 2) Filter logic (same as before)
  const servicesCopy = services && services.length ? [...services] : [];
  const filterByCat =
    servicesCopy.length && contentData?.categoryId
      ? servicesCopy.filter(
          (srv) => srv.serviceCategoryId === contentData.categoryId
        )
      : servicesCopy;

  const selectedCats = contentData?.selectedCategories || [];
  const filteredCategories =
    selectedCats.length && categories
      ? categories.filter((cat) => selectedCats.includes(cat.id))
      : categories || [];

  const renderList =
    productType === "categories"
      ? filteredCategories.length
        ? filteredCategories
        : categories
      : filterByCat;

  const limitedList = maxServiceShow ? renderList.slice(0, maxServiceShow) : renderList;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (!limitedList || limitedList.length === 0) {
    return null;
  }

  // 3) Carousel breakpoints
  const breakPoints = [
    { width: 0, itemsToShow: 1 },
    { width: 600, itemsToShow: 2 },
    { width: 900, itemsToShow: 3 },
    { width: 1200, itemsToShow: 5 },
  ];

  return (
    <Box
      className={classes.rootContainer}
      style={{
        ...(backgroundStyle === "image" && backgroundImage
          ? {
              backgroundImage: `url(${backgroundImage})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
            }
          : backgroundStyle === "gradient"
          ? { backgroundImage: backgroundColor }
          : { backgroundColor: backgroundColor || "#FFFFFF" }),
      }}
    >
      <SectionLayout paddingTop={paddingTop} paddingBottom={paddingBottom}>
        {/* Centered Headings */}
        <Box className={classes.headingsContainer}>
        <SectionSubtitle visibleHorizontalLine={false} pFontSize={subTitleFontSize} color={subTitleFontColor} mainColor={mainColor}>
            {_title}
          </SectionSubtitle>
          <SectionTitle pFontSize={titleFontSize} color={titleFontColor} variant={titleFormat}>{_subTitle}</SectionTitle>
        </Box>

        {/* Carousel */}
        <Box className={classes.carouselWrapper}>
          <Carousel
            breakPoints={breakPoints}
            pagination={false}
            itemPadding={[10, 10]}
            renderArrow={(arrowProps) => {
              const { type, onClick, isEdge } = arrowProps;
              return type === "PREV" ? (
                <IconButton
                  onClick={onClick}
                  disabled={isEdge}
                  className={classes.arrowButtonPrev}
                  size="small"
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  onClick={onClick}
                  disabled={isEdge}
                  className={classes.arrowButtonNext}
                  size="small"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              );
            }}
          >
            {limitedList.map((item, idx) => {
              // Determine localized name
              const displayName =
                chatgptContent && Object.keys(chatgptContent).length
                  ? isRTL
                    ? chatgptContent.arabic[`section${index}`][idx].title
                    : chatgptContent.english[`section${index}`][idx].title
                  : item.name;

              // Pass down a “service” prop with name + image
              const serviceWithName = {
                ...item,
                name: displayName,
                description:
                  chatgptContent && Object.keys(chatgptContent).length
                    ? isRTL
                      ? chatgptContent.arabic[`section${index}`][idx].description
                      : chatgptContent.english[`section${index}`][idx].description
                    : item.description,
              };

              return (
                <Box style={{ height: "100%", display: "flex" }}>
                  <ServiceCard
                    key={idx}
                    service={serviceWithName}
                    productType={productType}
                    companyId={companyId}
                    contentData={contentData}
                  />
                </Box>
              );
            })}
          </Carousel>
        </Box>

        {/* “View All →” Button */}
        <Box className={classes.viewAllWrapper}>
          <Button
            disableElevation
            className={classes.viewAllButton}
            onClick={() =>
              productType === "categories"
                ? handleExploreCategories()
                : handleExploreServices()
            }
          >
            {t && t("services.viewAll")}
          </Button>
        </Box>
      </SectionLayout>
    </Box>
  );
}

function ServiceCard({ service, productType, companyId, contentData }) {
  const classes = useStyles();
  const { data } = useCompanyData();
  const theme = useTheme();
  const { handleClick } = useServiceCard({ service, productType, companyId });

  const titleFontSize = contentData?.cardTitleFontSize ? contentData?.cardTitleFontSize : '21';

  const titleFontColor = contentData?.cardTitleColor ? contentData?.cardTitleColor : '';
  
  const cardRadius = contentData?.cardRadius ? `${contentData?.cardRadius}px` : '0px'; 
  const cardBorder = contentData?.border || 'none'; 
  // Decide which image URL to show
  const imgSrc =
    productType === "categories"
      ? service.photo || NoImage
      : service.cover || NoImage;

  return (
    <Card
      className={classes.cardRoot}
      onClick={handleClick}
      elevation={0}
      style={{
        cursor: 'pointer',
        ...((contentData?.cardBackgroundStyle === 'image' && contentData?.cardBackgroundImage) ? 
          {
              backgroundSize: "cover",
              backgroundImage: `url(${contentData?.cardBackgroundImage})`,
              backgroundRepeat: "no-repeat",
          } 
        : 
        (contentData?.cardBackgroundStyle === 'gradient' && contentData?.cardBackgroundColor) ? 
          {
              backgroundImage: contentData?.cardBackgroundColor ? contentData?.cardBackgroundColor : '#ffffff',
          } 
        :
        (contentData?.cardBackgroundStyle === 'solidColor' && contentData?.cardBackgroundColor) ? 
          {
              backgroundColor: contentData?.cardBackgroundColor || 'transparent',
          } 
        : 
          {backgroundColor: 'transparent'}
        ),
        overflow:'hidden'
      }}
      border={cardBorder}
      borderRadius={cardRadius}
    >
      <Box className={classes.avatarWrapper}>
        <Avatar
          alt={service.name || "category"}
          src={imgSrc}
          className={classes.avatarImage}
        />
      </Box>
      <CardContent style={{ padding: 0 }}>
        <Box className={`${classes.cardTitleWrapper} ${classes.underline}`}>
            <Typography
                variant={textVariant?.[titleFontSize]}
                className={`twoLineTxt ${classes.cardTitle} ${textClasses?.[titleFontSize]}`}
                style={titleFontColor ? { color: titleFontColor } : {}}
            >
                {service?.name}
            </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}